import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import Seo, { breadcrumbSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { BookButton } from "@/components/ui-bits";
import NotFound from "@/pages/NotFound";
import { ROOMS, HOTEL } from "@/lib/site";

export default function RoomDetail() {
  const { slug } = useParams();
  const room = ROOMS.find((r) => r.slug === slug);
  const [active, setActive] = useState(0);
  if (!room) return <NotFound />;

  const roomSchema = {
    "@context": "https://schema.org",
    "@type": "HotelRoom",
    name: room.name,
    description: room.seoDescription,
    bed: room.beds,
    occupancy: { "@type": "QuantitativeValue", maxValue: room.sleeps },
    amenityFeature: room.amenities.map((a) => ({ "@type": "LocationFeatureSpecification", name: a })),
  };

  return (
    <>
      <Seo
        title={`${room.name} | Regency Inn & Suites, Pittsburg KS`}
        description={room.seoDescription}
        path={`/rooms/${room.slug}`}
        image={room.images[0]}
        schema={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Rooms", path: "/rooms" },
            { name: room.name, path: `/rooms/${room.slug}` },
          ]),
          roomSchema,
        ]}
      />
      <div className="pt-28 pb-24 bg-bone">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <nav className="text-xs uppercase tracking-widest text-stone flex gap-2 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber">Home</Link>/
            <Link to="/rooms" className="hover:text-amber">Rooms</Link>/
            <span className="text-amber">{room.name}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="img-zoom aspect-[4/3] rounded-md" data-testid="room-gallery-main">
                <img src={room.images[active]} alt={`${room.name} at Regency Inn & Suites`} className="w-full h-full object-cover" />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                {room.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActive(i)}
                    data-testid={`room-thumb-${i}`}
                    className={`aspect-[4/3] rounded overflow-hidden border-2 transition-colors ${
                      active === i ? "border-amber" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt={`${room.name} view ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>

            <Reveal>
              <span className="text-xs uppercase tracking-[0.25em] text-amber-deep">{room.tagline}</span>
              <h1 className="font-serif text-5xl text-forest mt-3">{room.name}</h1>
              <div className="flex flex-wrap gap-x-8 gap-y-2 mt-5 text-sm text-stone border-y border-stone/15 py-4">
                <span className="flex items-center gap-2"><Icons.BedDouble size={16} className="text-amber" /> {room.beds}</span>
                <span className="flex items-center gap-2"><Icons.Users size={16} className="text-amber" /> Sleeps {room.sleeps}</span>
                <span className="flex items-center gap-2"><Icons.Maximize size={16} className="text-amber" /> {room.size}</span>
              </div>
              <p className="mt-6 text-stone leading-relaxed text-lg">{room.description}</p>

              <h2 className="font-serif text-2xl text-forest mt-8 mb-4">In this room</h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {room.amenities.map((a) => (
                  <li key={a} className="flex items-center gap-3 text-stone text-sm">
                    <Icons.Check size={16} className="text-amber shrink-0" /> {a}
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-wrap gap-4">
                <BookButton label="Book This Room" testid="roomdetail-book" />
                <a
                  href={HOTEL.phoneHref}
                  data-testid="roomdetail-call"
                  className="inline-flex items-center rounded-full border border-forest text-forest px-8 py-4 text-sm font-medium hover:bg-forest hover:text-bone transition-colors"
                >
                  Call to Reserve
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </>
  );
}
